import { FC, useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { artistsActions } from '@maestra/core/store/slices/artists';
import { fetchSubscriptionStatus } from '@maestra/core/store/slices/subscription';
import { useEntitlements } from '@maestra/core/hooks/useEntitlements';
import { perfilEstaAtivo } from '@maestra/core/hooks/useArtistCapabilities';
import { PAYWALL_DISABLED } from '@maestra/core/constants/maestra';
import { ModuloTravadoLigado } from '../ui-carbon/telas/dinheiro/Ligadas';
import type { LockedFeatureKey } from '@maestra/core/constants/bloqueios';
import { CarregandoApp, CarregandoPagina } from '../ui-carbon/padroes/Carregando';
import { LEITURA_PARA_MEMBRO, LIBERADO_PELO_CODIGO, MODULO_DO_BLOQUEIO, membroSemAcesso, type AreaDoAcesso } from '../logica/casca/acessoDosModulos';

// Trava as telas do artista enquanto o perfil não estiver ATIVO — por compra legada (vitalícia)
// ou por estar dentro da cota de perfis de uma assinatura PRO.
//
// ⚠️ ELE MOSTRA O BLOQUEIO, NÃO EXPULSA.
//
// Antes este gate fazia `Navigate` para /planos, e isso criava duas coisas ruins. A primeira era
// factual: a própria /planos promete que o plano grátis "visualiza músicas, agenda e equipe", e
// o app desmentia a página de preços ao expulsar quem tentava. A segunda era comercial: quem é
// jogado para fora nunca vê o que está comprando, e um paywall que esconde o produto vende pior
// que um que o mostra vazio.
//
// Agora a pessoa ENTRA: o layout do app fica em volta, a navegação continua lá, e no lugar do
// conteúdo aparece a tela do módulo com o que ele faz e o preço. Ela vê o sistema e o que a
// espera nele.
//
// A home do artista e o diagnóstico seguem fora do gate: são o que o grátis já tem de fato.
// Módulos que EXISTEM em somente-leitura para o convidado. São os que têm conteúdo já gravado
// para mostrar; os demais só fariam sentido produzindo algo novo.

/**
 * `espera`: 'pagina' dentro da casca (o esqueleto, com a casca na tela); 'app' nas telas cheias
 * fora dela (o wizard, o refazer, o editor), que esperam com a marca, como a abertura.
 */
export const RequireArtistActive: FC<{
  feature: LockedFeatureKey;
  /** A página do acesso do convidado, quando é mais estreita que a do bloqueio (Branding, Material). */
  pagina?: AreaDoAcesso;
  espera?: 'pagina' | 'app';
}> = ({ feature, pagina, espera = 'pagina' }) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const artist = useAppSelector((s) => s.artists.items.find((a) => a.id === id));
  const loaded = useAppSelector((s) => s.artists.loaded);
  const loading = useAppSelector((s) => s.artists.loading);
  const subInitialized = useAppSelector((s) => s.subscription.initialized);
  const subLoading = useAppSelector((s) => s.subscription.loading);
  const { isPro } = useEntitlements();

  // Garante a lista de artistas em acesso direto por URL (ex.: refresh em /artists/:id/catalog).
  // Sem isso o componente fica em spinner infinito, porque os filhos — que normalmente disparam
  // fetchArtists via useArtist — nunca chegam a montar.
  useEffect(() => {
    if (user?.id && !loaded && !loading) {
      dispatch(artistsActions.fetchArtists(user.id));
    }
  }, [user?.id, loaded, loading, dispatch]);

  // E garante a assinatura pelo mesmo motivo, com um agravante: quem busca o status é o
  // AppLayout, e o Wizard e o "refazer diagnóstico" correm FORA dele (tela cheia). Sem este
  // dispatch, abrir o Wizard direto pela URL nunca saía do spinner.
  useEffect(() => {
    if (user?.id && !PAYWALL_DISABLED && !subInitialized && !subLoading) {
      dispatch(fetchSubscriptionStatus());
    }
  }, [user?.id, subInitialized, subLoading, dispatch]);

  if (!loaded || (!PAYWALL_DISABLED && !subInitialized)) {
    return espera === 'app' ? <CarregandoApp /> : <CarregandoPagina />;
  }
  if (!artist) {
    return <Navigate to="/artists" replace />;
  }
  // ⚠️ O GATE DEIXOU DE SER BINÁRIO, e tem três saídas.
  //
  // Para o DONO nada muda: sem assinatura, ele vê o que o módulo faz e o convite para assinar.
  //
  // Para o CONVIDADO de um titular sem assinatura, o certo é outro. Ele não é o pagador — se
  // assinar o PRO continua bloqueado, porque o gate é o `owner_is_pro` — então vender a ele é
  // cobrar sem entregar. E a regra de produto é que ele ENTRE e LEIA: fica sem caneta, não sem
  // acesso. O aviso aparece quando ele tenta salvar (`AvisoDoTitular`).
  //
  // Os módulos sem nada a ler ficam de fora dessa passagem: o wizard CRIA, "refazer" recalcula e
  // a Nyta gasta token cobrado de quem conversa. Deixar entrar ali não seria leitura, seria
  // trabalho que alguém teria de pagar.
  // O ACESSO POR MÓDULO vem antes da assinatura: se o titular não liberou o módulo, o motivo a
  // dizer é esse, e não a assinatura dele.
  if (membroSemAcesso(artist, pagina ?? MODULO_DO_BLOQUEIO[feature])) {
    return <ModuloTravadoLigado feature={feature} papel='sem-acesso' />;
  }
  if (!perfilEstaAtivo(artist, isPro)) {
    const ehMembro = artist.role === 'member';
    if (ehMembro && LEITURA_PARA_MEMBRO.has(feature)) return <Outlet />;
    // O código de acesso libera o planejamento e o plano de ação (e só eles): o dono entra no wizard
    // e no plano; os módulos seguem travados (decisão do Lucas, 27/09/2026).
    if (!ehMembro && artist.liberado_por_codigo && LIBERADO_PELO_CODIGO.has(feature)) return <Outlet />;
    return <ModuloTravadoLigado feature={feature} papel={ehMembro ? 'membro' : 'dono'} />;
  }
  return <Outlet />;
};

export default RequireArtistActive;
